import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { getGradeLabel, GRADE_ORDER, normalizeGrade } from "utils/standards";
import { useLessonQuestions } from "hooks/useCurriculum/useCurriculum";
import { ExampleQuestionsCarousel } from "components/shared/ExampleQuestionsCarousel/ExampleQuestionsCarousel";
import { SEARCH_TYPE, useSearchResultTracking } from "hooks/semanticSearchAnalytics";
import "components/pages/GroupCodeStandardSearch/groupCodeStandardSearch.css";
import arrow_down from "assets/Arrow_Down.svg";
import arrow_right from "assets/Arrow_Right.svg";

// Only mounted while the accordion is open, so the fetch starts on expand and the
// unexpanded list stays free of React Query. Unfiltered, like the detail panel's
// core carousel on a freshly selected lesson.
const LessonResultQuestions = ({ lessonId }) => {
  const { questions, total, loading, loadMore } = useLessonQuestions(lessonId);

  return (
    <div style={{ backgroundColor: colors.milkGlass500 }}>
      {!loading && questions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px" }}>
          No questions available for this lesson.
        </div>
      ) : (
        <ExampleQuestionsCarousel
          questions={questions}
          loading={loading}
          total={total}
          onLoadMore={loadMore}
        />
      )}
    </div>
  );
};

// Module scope, not inline: declared inside the parent it would be a new component
// type on every render, so toggling a grade filter would collapse every card.
const LessonResultCard = ({ result, onResultClick }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="search-result-card">
      <button
        type="button"
        className="ls-lesson-result-button"
        onClick={() => onResultClick(result)}
      >
        <span className="standard-code">{result.ordinal_name || "LESSON"}</span>
        <span className="standard-title-sm">{result.name}</span>
        {/* A result can sit in a different course than the one on screen,
            so the course/grade has to be legible before clicking. */}
        <span className="ls-lesson-result-context">
          {[
            result.unit_ordinal_name,
            result.unit_name,
            // Its own entry, not a pre-joined string: interpolated, a missing
            // course_name would render as the literal "undefined ()" instead of
            // dropping out the way the unit fields do.
            result.course_name &&
              `${result.course_name}${result.grade_level ? ` (${getGradeLabel(result.grade_level)})` : ""}`,
          ]
            .filter(Boolean)
            .join(" · ")}
          {result.is_optional ? " · Optional" : ""}
        </span>
      </button>

      <div className="detail-section">
        <button
          type="button"
          className="section-header ls-result-questions-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <div className="section-header-content">
            <img
              src={expanded ? arrow_down : arrow_right}
              alt="arrow"
              style={{ width: "10px" }}
            />
            <span className="search-results-view-questions">View Questions</span>
          </div>
        </button>
        {expanded && <LessonResultQuestions lessonId={result.lesson_id} />}
      </div>
    </div>
  );
};

// Lesson results for the Assign Lesson flow, rendered inline in the Lesson view
// the same way SearchResultsView renders standards in the Standard view, down to
// the per-result "View Questions" accordion.
export const LessonSearchResultsView = ({
  searchResults,
  onResultClick,
  onBackClick,
}) => {
  const [selectedGrades, setSelectedGrades] = useState([]);
  const trackResultClick = useSearchResultTracking(SEARCH_TYPE.LESSON);

  // Results deliberately cross grades — that is the point of semantic search —
  // so a grade filter is how a teacher narrows back down.
  const availableGrades = useMemo(() => {
    const grades = new Set();
    searchResults.forEach((result) => {
      if (result.grade_level) grades.add(normalizeGrade(result.grade_level));
    });
    return Array.from(grades).sort(
      (a, b) => GRADE_ORDER.indexOf(a) - GRADE_ORDER.indexOf(b),
    );
  }, [searchResults]);

  // A new query keeps this view mounted, so a grade picked for the previous
  // results survives into the next ones. Only grades the current results still
  // offer count: otherwise the filter is on with no chip to show for it, and an
  // unrelated search reads as "No lessons found for selected grade".
  const activeGrades = useMemo(
    () => selectedGrades.filter((grade) => availableGrades.includes(grade)),
    [selectedGrades, availableGrades],
  );

  const filteredResults = useMemo(() => {
    if (activeGrades.length === 0) return searchResults;
    return searchResults.filter(
      (result) =>
        result.grade_level &&
        activeGrades.includes(normalizeGrade(result.grade_level)),
    );
  }, [searchResults, activeGrades]);

  const toggleGrade = (grade) => {
    setSelectedGrades((prev) =>
      prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade],
    );
  };

  return (
    <div className="search-results-view">
      <div className="search-results-header">
        <button className="back-to-tree-button" onClick={onBackClick}>
          <FontAwesomeIcon icon={faChevronLeft} />
          Search Results
        </button>
      </div>

      {availableGrades.length > 0 && (
        <div className="grade-filter-container">
          {availableGrades.map((grade) => (
            <button
              key={grade}
              className={`grade-filter-button ${activeGrades.includes(grade) ? "active" : ""}`}
              onClick={() => toggleGrade(grade)}
            >
              {getGradeLabel(grade).toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {filteredResults.length === 0 ? (
        <div className="no-results">
          {activeGrades.length > 0
            ? `No lessons found for selected grade${activeGrades.length > 1 ? "s" : ""}.`
            : "No lessons found."}
        </div>
      ) : (
        filteredResults.map((result, index) => (
          <LessonResultCard
            key={result.lesson_id}
            result={result}
            onResultClick={(clicked) => {
              trackResultClick(index + 1, { lesson_id: clicked.lesson_id });
              onResultClick(clicked);
            }}
          />
        ))
      )}
    </div>
  );
};
